import { BenefitLayout } from "providers/benefit-layout";
import React, { FC, useEffect, useState } from "react";
import { useForm } from "shared/hooks/use-form";
import { Input } from "shared/components/forms/Input";
import { rentAmountValidations } from "views/rent-reporting-id-issues-m2m-lease-end/validations";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import api from "api";
import { toast } from "react-toastify";
import { Button, ButtonVariant } from "shared/components/Button";
import { SuccessScreenView } from "../../../../components/SuccessScreen";
import { View } from "views/rent-reporting-id-issues-m2m-lease-end/types";
import { Money } from "types/money";

type Props = {
  leaseEndDate: string;
  rentAmount: Money;
  setView: (view: View) => void;
};

enum Steps {
  ConfirmRentAmount = "ConfirmRentAmount",
  Success = "Success"
}

export const LeaseWillBeM2MView: FC<Props> = ({
  leaseEndDate,
  rentAmount,
  setView
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState<Steps>(Steps.ConfirmRentAmount);

  const { register, onSubmit, handleChange } = useForm(
    {
      rentAmount: ""
    },
    rentAmountValidations
  );

  const { mutate: renewLeaseDetails } = useMutation({
    mutationKey: ["UPDATE_LEASE_DETAILS"],
    mutationFn: ({
      monthly_rent_amount
    }: {
      monthly_rent_amount: {
        cents: number;
        currency: "USD";
      };
    }) =>
      api.renewLeaseOptions({
        id: Number(id),
        month_to_month_lease: true,
        pending_monthly_rent_amount: monthly_rent_amount
      }),
    onError: (error) => {
      toast.error(error.message || "An error occurred");
    },
    onSuccess: () => {
      setStep(Steps.Success);
    }
  });

  useEffect(() => {
    handleChange("rentAmount", () => (rentAmount.cents || 0) / 100)({});
  }, [rentAmount]);

  if (step === Steps.ConfirmRentAmount)
    return (
      <BenefitLayout
        hasBack={true}
        title="Please confirm your lease details"
        mainClassName="max-w-[440px] w-full mx-auto md:mt-4"
        footer={
          <div className="max-w-[440px] mx-auto w-full">
            <Button
              variant={ButtonVariant.PRIMARY}
              className="w-full"
              type="submit"
              form="confirm-details-form"
            >
              Confirm rent amount
            </Button>

            <Button
              className="w-full md:block hidden mt-2"
              onClick={() => setView(View.SelectView)}
            >
              Back
            </Button>
          </div>
        }
      >
        <form
          id="confirm-details-form"
          onSubmit={onSubmit({
            onSuccess: (data) => {
              renewLeaseDetails({
                monthly_rent_amount: {
                  cents: data.rentAmount * 100,
                  currency: "USD"
                }
              });
            },
            onError: (e) => {
              toast.error(Object.values(e).join(", "));
            }
          })}
        >
          <Input
            id="rentAmount"
            label="Rent amount"
            placeholder="Rent amount"
            {...register("rentAmount")}
            type="money"
            hideOptional
          />
        </form>
      </BenefitLayout>
    );

  return (
    <SuccessScreenView
      title="Great!"
      subtext1={`On ${leaseEndDate} you will see these changes reflected in your app.`}
      buttonTitle="Done"
      onButtonClick={() => navigate("/hub")}
    />
  );
};
